// View state — the author's working set, persisted per device (pre-sync).
//
// U46 (master audit P12): the open tabs and the active object survive a full
// reload. Like the theme and the display name, this is DEVICE state, not a
// domain fact (memory project_theme_outside_event_log): it round-trips
// through localStorage and is never emitted as an event; when the sync
// substrate lands, per-device view state stays local by design. Only the
// impure shell (AppShellHost) calls `readViewState`/`writeViewState`; the
// parse/serialize pair is pure and unit-tested.
//
// WORKSPACE-FLAT (flattened-object-model-design.md): the persisted unit is
// ONE `PaneTree` — the split-views substrate — for the whole workspace, not a
// map keyed by project. Before the flatten this was `{ activeProjectId,
// paneTrees: Map<ProjectId, PaneTree> }`; with Project retired as a
// container there is nothing left to key by, so the map collapses to its one
// remaining entry. **Destructive wire-shape change:** the old envelope had a
// `paneTrees` (plural) field; this shape has `paneTree` (singular). Pre-MVP
// allows this with no migration (project_migration_squash_at_first_stable) —
// `parseViewState`'s shape check below naturally degrades any earlier blob
// (which lacks `paneTree`) to a fresh session rather than half-parsing it.
//
// Parse, don't validate (the theme.tsx pattern): localStorage hands back
// `string | null` written by ANY earlier build. Every field is re-established
// at this boundary; `parsePaneTree` (paneTree.ts §3.4) degrades any
// malformed/partial/future tree to a fresh home tree rather than crashing.
// Staleness beyond shape — ids whose objects no longer exist — is reconciled
// against the loaded world by the host (`reconcile-tabs`), not here.
//
// Dirty marks are deliberately NOT persisted (serializePaneTree writes every
// resource tab clean): unsaved content does not survive a reload, so a
// restored tab claiming unsaved changes would lie.

import {
    emptyPaneTree,
    type PaneTree,
    parsePaneTree,
    serializePaneTree,
} from "../components/Navigation/paneTree.js";
import { readDeviceKey, writeDeviceKey } from "./deviceStore.js";

// Namespaced key, matching the `theme.tsx` convention on shared origins.
const STORAGE_KEY = "bilda.viewState";

// What survives a reload: the workspace's ONE remembered pane tree.
export type ViewState = {
    readonly paneTree: PaneTree;
};

export const emptyViewState: ViewState = { paneTree: emptyPaneTree };

// --- Parsing (pure) ----------------------------------------------------------

// The whole stored envelope. Exported for tests; `readViewState` is the impure
// wrapper the host uses. An earlier build's `{ activeProjectId, paneTrees }`
// envelope has no `paneTree` key, so it falls through to `emptyViewState`
// exactly like corrupted storage — the graceful-degradation path both share.
export const parseViewState = (raw: string | null): ViewState => {
    if (raw === null) return emptyViewState;
    let data: unknown;
    try {
        data = JSON.parse(raw);
    } catch {
        // Corrupted storage is a fresh session, not a crash.
        return emptyViewState;
    }
    if (typeof data !== "object" || data === null) return emptyViewState;
    if (!("paneTree" in data)) return emptyViewState;
    return { paneTree: parsePaneTree(data.paneTree) };
};

// --- Serializing (pure) ------------------------------------------------------

// The inverse of `parseViewState`.
export const serializeViewState = (state: ViewState): string =>
    JSON.stringify({ paneTree: serializePaneTree(state.paneTree) });

// --- The impure seam (host-only, like displayName.ts) ------------------------

export const readViewState = (): ViewState =>
    parseViewState(readDeviceKey(STORAGE_KEY));

export const writeViewState = (state: ViewState): void =>
    writeDeviceKey(STORAGE_KEY, serializeViewState(state));
